import { useCallback } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { Json } from '@kit/supabase/database';
import { useSupabase } from '@kit/supabase/hooks/use-supabase';

/** Fetches the user's personal account (their own name and photo). */
export function usePersonalAccountData(
  userId: string,
  partialAccount?: {
    id: string | null;
    name: string | null;
    picture_url: string | null;
    public_data?: Json;
  },
  options?: {
    enabled?: boolean;
  },
) {
  const client = useSupabase();
  const queryKey = ['account:data', userId];

  const queryFn = async () => {
    if (!userId) {
      return null;
    }

    // The user's own personal account. Reading the first membership here
    // picked up the workspace (e.g. "travelmore") and the name form then
    // edited the workspace name instead of the person's.
    const response = await client
      .from('accounts')
      .select('id, name, picture_url, public_data')
      .eq('primary_owner_user_id', userId)
      .eq('is_personal_account', true)
      .limit(1)
      .maybeSingle();

    if (response.error) {
      throw response.error;
    }

    return response.data;
  };

  return useQuery({
    queryKey,
    queryFn,
    enabled: !!userId && (options?.enabled ?? true),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    initialData: partialAccount?.id
      ? {
          id: partialAccount.id,
          name: partialAccount.name,
          picture_url: partialAccount.picture_url,
          public_data: partialAccount.public_data,
        }
      : undefined,
  });
}

export function useRevalidatePersonalAccountDataQuery() {
  const queryClient = useQueryClient();

  return useCallback(
    (userId: string) =>
      queryClient.invalidateQueries({
        queryKey: ['account:data', userId],
      }),
    [queryClient],
  );
}
